import React, { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { WithdrawalRequest } from '../../../types';
import { useApproveWithdrawal, useResolveFraudFlag, useFraudFlags } from '../../../hooks/useApi';
import { LoadingSpinner } from '../../ui/Loading';
import { getWithdrawalListLabel } from '../../../utils/withdrawalRequester';
import { formatWithdrawalDisplayAmount } from '../../../utils/portalUsdWithdrawalDisplay';

interface ApproveModalProps {
  withdrawal: WithdrawalRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred while approving withdrawal';
};

const ApproveModal: React.FC<ApproveModalProps> = ({
  withdrawal,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [adminNotes, setAdminNotes] = useState('');
  const approveWithdrawalMutation = useApproveWithdrawal();
  const resolveFraudFlagMutation = useResolveFraudFlag();

  // Fetch associated fraud flags for this withdrawal
  const { data: fraudFlagsData } = useFraudFlags(
    withdrawal
      ? { transactionId: withdrawal.transactionId, isResolved: false }
      : undefined
  );

  const unresolvedFlags = fraudFlagsData?.data?.fraudFlags || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawal) return;

    try {
      // 1. Approve the withdrawal
      await approveWithdrawalMutation.mutateAsync({
        transactionId: withdrawal.transactionId,
        data: {
          adminNotes: adminNotes.trim() || undefined,
        },
      });

      // 2. Resolve all associated fraud flags
      for (const flag of unresolvedFlags) {
        try {
          await resolveFraudFlagMutation.mutateAsync({
            flagId: flag.id,
            data: {
              notes: `Approved - ${adminNotes.trim() || 'Legitimate withdrawal'}`,
            },
          });
        } catch (err) {
          console.warn('Failed to resolve fraud flag:', flag.id, err);
        }
      }

      toast.success('Withdrawal approved successfully');
      onSuccess?.();
      onClose();
      setAdminNotes('');
    } catch (error: any) {
      console.error('Approve withdrawal error:', error);
      toast.error(parseErrorMessage(error));
    }
  };

  const handleClose = () => {
    setAdminNotes('');
    onClose();
  };

  if (!isOpen || !withdrawal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Approve Flagged Withdrawal</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {withdrawal.provider === 'linkmepay' && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm"
                role="alert"
              >
                <p className="font-medium">LinkMe Pay</p>
                <p className="mt-1">
                  You need to go to LinkMe Pay&apos;s payout management page to approve before the funds will be sent to the user&apos;s wallet.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to approve this flagged withdrawal? This will also resolve
                all associated fraud flags.
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm">
                  <div className="font-medium font-mono text-xs">{withdrawal.transactionId}</div>
                  <div className="text-muted-foreground">{getWithdrawalListLabel(withdrawal)}</div>
                  <div className="font-medium text-red-600">
                    {formatWithdrawalDisplayAmount(withdrawal, { signed: true })}
                  </div>
                </div>
              </div>
              {unresolvedFlags.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg">
                  <p className="text-xs font-medium text-orange-800">
                    {unresolvedFlags.length} unresolved fraud flag(s) will be marked as resolved.
                  </p>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="adminNotes" className="block text-sm font-medium mb-2">
                Admin Notes (Optional)
              </label>
              <Input
                id="adminNotes"
                type="text"
                placeholder="e.g., Reviewed profit analysis. User won legitimately..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {adminNotes.length}/1000 characters
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={approveWithdrawalMutation.isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {approveWithdrawalMutation.isLoading ? (
                  <div className="flex items-center">
                    <LoadingSpinner />
                    <span className="ml-2">Approving...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Withdrawal
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApproveModal;
